import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { Backdrop, CircularProgress } from "@mui/material";
import { loginRequest } from "./utils/msalInstance";
import LoginHeader from "./components/LoginHeader.js";
import SignIn from "./components/SignIn.js";
import config from "./config";
import { refusalMessage, mayEnter } from "./utils/loginRefusal";
import { clearPermissionsCache } from "./hooks/usePermissions";
import { clearSessionState } from "./utils/logout";

export default function Home() {
  const navigate = useNavigate();
  const location = useLocation();
  const { instance } = useMsal();
  const [authError, setAuthError] = useState(location.state?.authError || "");
  const [loading, setLoading] = useState(false);

  async function getOrganization(username) {
    try {
      const headers = new Headers();
      headers.append("username", username);
      const response = await fetch(
        `${process.env.REACT_APP_BASE_API_URL}organization-name`,
        { method: "GET", headers, redirect: "follow" }
      );
      if (response.status === 200) {
        const json = await response.json();
        if (json.success !== false && json.status !== 400) {
          localStorage.setItem("organization", json.data.name);
          localStorage.setItem("features", json.data.enabledFeatures);
        }
      }
    } catch (err) {
      console.error(err);
    }
  }

  async function getCompanyId(username) {
    try {
      const headers = new Headers();
      headers.append("username", username);
      const response = await fetch(
        `${process.env.REACT_APP_BASE_API_URL}getcompanyid`,
        { method: "GET", headers, redirect: "follow" }
      );
      if (response.status === 200) {
        const json = await response.json();
        if (json.success !== false && json.status !== 400) {
          localStorage.setItem("companyid", json.data);
        }
      }
    } catch (err) {
      console.error(err);
    }
  }


  // One answer for "may this person enter, and if not, why": the proxy verifies
  // the token, checks the tenant allow-list and the person's roles, and returns
  // { roles, permissions, staff, reason }. Staff bypass so they can onboard the
  // first users. Throws on a transport/5xx failure so the caller can say "try again"
  // rather than telling someone their account doesn't exist.
  const getLoginState = async (email, token) => {
    const headers = new Headers();
    headers.append("email", email);
    // Login-time call: runs before loginType='AAD' is set, so the global fetch
    // interceptor can't attach the token yet — pass it from the MSAL result.
    if (token) headers.append("Authorization", `Bearer ${token}`);
    const response = await fetch(
      `${process.env.REACT_APP_BASE_API_URL}my-permissions`,
      { method: "GET", headers, redirect: "follow" }
    );
    if (response.status === 200) return response.json();
    let detail = "";
    try { detail = (await response.json()).error || ""; } catch (_) {}
    const err = new Error(detail || `Sign-in check failed (${response.status})`);
    err.status = response.status;
    throw err;
  };

  const mapAadError = (err) => {
    const code = err?.errorCode || "";
    const msg = err?.errorMessage || err?.message || "";
    if (/user_cancelled|interaction_in_progress/i.test(code)) return "";
    if (/consent_required|AADSTS65004|AADSTS90094|access_denied/i.test(code + " " + msg)) {
      return "Your organization hasn’t approved EcoPrism for Microsoft sign-in yet. Ask your Microsoft administrator to grant access.";
    }
    return "Microsoft sign-in failed. Please try again, or contact support if it continues.";
  };

  // Post-auth provisioning gate, run when we land back on /callback. The proxy
  // already rejected an unknown tenant (403) or an un-added person (empty roles);
  // here we only choose the words and establish the session.
  const handleAadResult = async (result) => {
    const email = result.account.username;
    let state;
    try {
      state = await getLoginState(email, result.idToken);
    } catch (e) {
      const authError =
        e.status === 403
          ? refusalMessage("tenant_not_onboarded", { supportEmail: config.supportEmail })
          : "We couldn't verify your account right now. Please try again in a moment.";
      navigate("/login", { replace: true, state: { authError } });
      return;
    }
    if (!mayEnter(state)) {
      navigate("/login", { replace: true, state: { authError: refusalMessage(state.reason, { supportEmail: config.supportEmail }) } });
      return;
    }
    // Drop whatever the previous sign-in left behind so this session never shows
    // another account's organisation or menu.
    clearSessionState();
    clearPermissionsCache();
    localStorage.setItem("userName", email);
    localStorage.setItem("loginType", "AAD");
    localStorage.setItem("JWT", result.idToken);
    await getOrganization(email);
    await getCompanyId(email);
    navigate("/home", { replace: true });
  };

  // AD sign-in uses loginRedirect, not loginPopup: a browser COOP boundary on the
  // Microsoft hop blocks MSAL's popup window handshake, hanging the popup.
  const handleMicrosoftLogin = async () => {
    setAuthError("");
    setLoading(true);
    try {
      await instance.loginRedirect({ ...loginRequest, prompt: "select_account" });
      // Navigates away; nothing after this runs on success.
    } catch (err) {
      setLoading(false);
      const authError = mapAadError(err);
      if (authError) setAuthError(authError);
    }
  };

  // Complete the redirect sign-in on return. handleRedirectPromise resolves the
  // response; if msal-react's provider already consumed the hash, recover the
  // account on /callback and mint a fresh id token.
  // Only handle the MSAL redirect when the browser is on the explicit
  // redirect callback path. This prevents mounting `/login` from attempting
  // a silent/redirect completion and causing an implicit sign-in.
  useEffect(() => {
    if (window.location.pathname !== "/callback") return undefined;
    let cancelled = false;
    (async () => {
      try {
        let result = await instance.handleRedirectPromise();
        if (!result) {
          const account = instance.getAllAccounts()[0];
          if (account) result = await instance.acquireTokenSilent({ ...loginRequest, account });
        }
        if (cancelled || !result) return;
        setLoading(true);
        await handleAadResult(result);
      } catch (err) {
        if (cancelled) return;
        setLoading(false);
        const authError = mapAadError(err);
        if (authError) setAuthError(authError);
      }
    })();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div id="wrapper">
      <Backdrop open={loading} sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <div className="login__wrapper">
        <section className="login__section">
          <div className="row">
            <div className="col-lg-12">
              <div className="login__inner">
                <div className="login__left">
                  <img className="login__left-img" src="./assets/img/login.png" alt="" />
                </div>
                <div className="login__right">
                  <h2 className="login__title">Sign in</h2>
                  <SignIn onLogin={(username, password) => console.log("Logging in:", username, password)} />
                  <div className="or__text">OR</div>
                  <button
                    className="btn google__btn"
                    onClick={handleMicrosoftLogin}
                    disabled={loading}
                  >
                    <img src="assets/img/microsoft_icon.png" alt="" />
                    {loading ? "Signing in…" : "Sign in with Microsoft"}
                  </button>
                  {authError && (
                    <p className="login__error" style={{ color: "#d32f2f", marginTop: 12 }}>
                      {authError}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
